import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NikoProjectExtended } from '../../types/project';
import { useCursorContext } from '../../hooks/useCursorContext';

gsap.registerPlugin(ScrollTrigger);

interface ProjectOverviewProps {
  project: NikoProjectExtended;
}

export function ProjectOverview({ project }: ProjectOverviewProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        gsap.fromTo(
          '.overview-animate',
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
          }
        );
        ScrollTrigger.refresh();
      }, sectionRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-8 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left: Overview text */}
          <div>
            <h2 className="overview-animate text-xs text-white/30 tracking-[0.3em] uppercase mb-6">
              Überblick
            </h2>
            <p className="overview-animate text-xl md:text-2xl text-white/70 leading-relaxed">
              {project.overview}
            </p>
          </div>

          {/* Right: Meta info */}
          <div className="grid grid-cols-2 gap-8">
            {/* Role */}
            <div className="overview-animate">
              <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
                Rolle
              </h3>
              <p className="text-lg text-white/80">{project.role}</p>
            </div>

            {/* Duration */}
            {project.duration && (
              <div className="overview-animate">
                <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
                  Dauer
                </h3>
                <p className="text-lg text-white/80">{project.duration}</p>
              </div>
            )}

            {/* Team Size */}
            {project.teamSize && (
              <div className="overview-animate">
                <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
                  Team
                </h3>
                <p className="text-lg text-white/80">{project.teamSize}</p>
              </div>
            )}

            {/* Website */}
            <div className="overview-animate">
              <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
                Website
              </h3>
              {project.url ? (
                <a
                  href={`https://${project.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg text-emerald-500 hover:text-emerald-400 transition-colors"
                  onMouseEnter={() => setCursorState('hovering')}
                  onMouseLeave={() => setCursorState('default')}
                >
                  {project.url} →
                </a>
              ) : (
                <p className="text-lg text-white/40">Intern / NDA</p>
              )}
            </div>
          </div>
        </div>

        {/* Tech Stack */}
        <div className="mt-16 pt-16 border-t border-white/10">
          <h3 className="overview-animate text-xs text-white/30 tracking-[0.3em] uppercase mb-6">
            Technologien
          </h3>
          <div className="flex flex-wrap gap-3">
            {project.tech.map((tech, i) => (
              <span
                key={tech}
                className="overview-animate px-5 py-2.5 text-sm text-white/60 border border-white/10 rounded-full hover:border-emerald-500/50 hover:text-emerald-500 transition-colors"
                style={{ transitionDelay: `${i * 0.05}s` }}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Deliverables */}
        {project.deliverables && project.deliverables.length > 0 && (
          <div className="mt-12">
            <h3 className="overview-animate text-xs text-white/30 tracking-[0.3em] uppercase mb-6">
              Lieferumfang
            </h3>
            <div className="flex flex-wrap gap-4">
              {project.deliverables.map((item) => (
                <span
                  key={item}
                  className="overview-animate text-sm text-white/50"
                >
                  • {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
